import React from 'react';
import { cn } from '~/lib/utils';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'rounded-lg border border-border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md',
      className
    )}
    {...props}
  />
));
Card.displayName = 'Card';

export function CardHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('space-y-1.5 p-6', className)} {...props} />;
}

export function CardContent({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-6 pb-6', className)} {...props} />;
}

export function CardTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cn('text-2xl font-semibold leading-none tracking-tight', className)} {...props} />;
}

export function CardDescription({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('text-sm text-muted-foreground', className)} {...props} />;
}

export interface BlogCardProps {
  title: string;
  description: string;
  href: string;
  date: string;
  readingTime?: string;
}

export function BlogCard({ title, description, href, date, readingTime }: BlogCardProps) {
  return (
    <a
      href={href}
      className="group block rounded-lg border border-border bg-card p-6 shadow transition-all hover:border-primary/30 hover:shadow-lg"
    >
      <p className="text-xs uppercase tracking-wide text-primary">{date}</p>
      <h3 className="mt-2 text-2xl font-semibold text-foreground transition group-hover:text-primary">
        {title}
      </h3>
      <p className="mt-3 text-sm text-muted-foreground">{description}</p>
      {readingTime ? (
        <p className="mt-4 text-xs text-muted-foreground">{readingTime} read</p>
      ) : null}
    </a>
  );
}

export interface ItemCardProps {
  name: string;
  summary: string;
  rarity: string;
  href: string;
}

export function ItemCard({ name, summary, rarity, href }: ItemCardProps) {
  return (
    <a
      href={href}
      className="group flex flex-col gap-3 rounded-lg border border-border bg-card p-5 shadow transition-all hover:border-primary/40 hover:shadow-md"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-foreground transition group-hover:text-primary">
          {name}
        </h3>
        <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium uppercase tracking-wide text-primary">
          {rarity}
        </span>
      </div>
      <p className="text-sm text-muted-foreground">{summary}</p>
    </a>
  );
}

export interface FeatureCardProps {
  title: string;
  description: string;
  icon?: React.ReactNode;
}

export function FeatureCard({ title, description, icon }: FeatureCardProps) {
  return (
    <div className="rounded-lg border border-border bg-card p-6 shadow-sm transition-all hover:shadow-md">
      {icon ? <div className="mb-4 text-primary">{icon}</div> : null}
      <h3 className="text-xl font-semibold text-foreground">{title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{description}</p>
    </div>
  );
}

export interface StatsCardProps {
  label: string;
  value: string;
  subtext?: string;
}

export function StatsCard({ label, value, subtext }: StatsCardProps) {
  return (
    <div className="rounded-lg border border-border bg-card p-6 text-center shadow-sm">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-2 text-3xl font-semibold text-foreground">{value}</p>
      {subtext ? <p className="mt-2 text-sm text-muted-foreground">{subtext}</p> : null}
    </div>
  );
}
